/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LinkButton, LogViewer, Progress } from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

// Mirrors the backend Change union; org selects the teams file.
export type Change =
  | { op: 'addMember'; org: string; team: string; login: string }
  | { op: 'removeMember'; org: string; team: string; login: string }
  | { op: 'setPermission'; org: string; slug: string; permission: string }
  | {
      op: 'editConfig';
      org: string;
      slug: string;
      description?: string;
      parentTeamId?: string;
      privacy?: string;
    }
  | {
      op: 'addTeam';
      org: string;
      slug: string;
      description: string;
      parentTeamId?: string;
      members: string[];
    }
  | { op: 'removeTeam'; org: string; slug: string }
  | {
      /** Named repos only -- the scoped counterpart of setPermission. */
      op: 'grantRepoAccess';
      org: string;
      slug: string;
      permission: string;
      repos: string[];
    }
  | {
      op: 'revokeRepoAccess';
      org: string;
      slug: string;
      repo: string;
    };

export type StagedChange = { id: number; label: string; change: Change };

type DiffFile = { org: string; path: string; before: string; after: string };
type Preview = { effective: number; noops: number; files: DiffFile[] };
type Phase =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'opening' }
  | { kind: 'done'; prUrl?: string; prNumber?: number }
  | { kind: 'error'; message: string };

// --- line diff (compact LCS; no dependency) ----------------------------------
type DiffLine = { t: 'ctx' | 'add' | 'del'; text: string; oldNo?: number; newNo?: number };

function lineDiff(before: string, after: string): DiffLine[] {
  const a = before.split('\n');
  const b = after.split('\n');
  const n = a.length;
  const m = b.length;
  // LCS length table.
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  let oldNo = 1;
  let newNo = 1;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ t: 'ctx', text: a[i], oldNo, newNo });
      i += 1;
      j += 1;
      oldNo += 1;
      newNo += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ t: 'del', text: a[i], oldNo });
      i += 1;
      oldNo += 1;
    } else {
      out.push({ t: 'add', text: b[j], newNo });
      j += 1;
      newNo += 1;
    }
  }
  while (i < n) {
    out.push({ t: 'del', text: a[i], oldNo });
    i += 1;
    oldNo += 1;
  }
  while (j < m) {
    out.push({ t: 'add', text: b[j], newNo });
    j += 1;
    newNo += 1;
  }
  return out;
}

// Collapse long unchanged runs to a fold marker, keeping CONTEXT lines around
// every change so the diff reads like a unified `git diff`.
const CONTEXT = 3;
type Row = DiffLine | { t: 'fold'; count: number };

function foldDiff(lines: DiffLine[]): Row[] {
  const keep = new Array(lines.length).fill(false);
  lines.forEach((l, idx) => {
    if (l.t !== 'ctx') {
      for (
        let k = Math.max(0, idx - CONTEXT);
        k <= Math.min(lines.length - 1, idx + CONTEXT);
        k += 1
      ) {
        keep[k] = true;
      }
    }
  });
  const rows: Row[] = [];
  let folded = 0;
  lines.forEach((l, idx) => {
    if (keep[idx]) {
      if (folded > 0) {
        rows.push({ t: 'fold', count: folded });
        folded = 0;
      }
      rows.push(l);
    } else {
      folded += 1;
    }
  });
  if (folded > 0) rows.push({ t: 'fold', count: folded });
  return rows;
}

const useStyles = makeStyles(theme => ({
  paper: {
    height: '82vh',
    // Fullscreen on a phone: at 390px the fixed 360px left pane consumed the
    // whole dialog and left the DIFF -- this product's core trust artifact --
    // zero pixels wide, while Open PR stayed clickable.
    [theme.breakpoints.down('xs')]: { height: '100%', margin: 0, maxHeight: '100%' },
  },
  body: {
    display: 'flex',
    gap: 0,
    padding: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: { flexDirection: 'column' },
  },
  left: {
    width: 360,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // Stacked: full width, capped height, and the divider moves underneath.
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxHeight: '45%',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  leftHeader: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 2, 0.5),
  },
  leftScroll: { overflowY: 'auto', flex: 1, padding: theme.spacing(0.5, 1) },
  // One staged change: org chip + a fully-wrapping label + remove. No truncation.
  changeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 6,
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:hover $changeRemove': { opacity: 1 },
  },
  orgChip: {
    height: 18,
    fontSize: '0.62rem',
    flexShrink: 0,
    marginTop: 1,
  },
  changeLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    overflowWrap: 'anywhere',
  },
  changeRemove: {
    padding: 4,
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity 120ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '&:focus-visible': { opacity: 1 },
  },
  summary: {
    fontSize: '0.8125rem',
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  right: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    // Stacked, the diff pane must still own real vertical space.
    [theme.breakpoints.down('xs')]: { minHeight: '45%' },
  },
  // Diff | Log segmented toggle at the top of the right pane.
  viewTabs: {
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'center',
  },
  viewTab: { fontSize: '0.75rem', height: 26, cursor: 'pointer' },
  // Result banner (success/error) shown above the log after Open PR.
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  bannerIconOk: { color: theme.palette.success.main, fontSize: 36, flexShrink: 0 },
  bannerIconErr: { color: theme.palette.error.main, fontSize: 36, flexShrink: 0 },
  bannerTitle: { fontSize: '0.9375rem', fontWeight: 600 },
  bannerBody: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  bannerAction: { marginLeft: 'auto', flexShrink: 0 },
  // LogViewer needs a sized flex parent to scroll within.
  logWrap: { flex: 1, minHeight: 0, position: 'relative' },
  fileTabs: {
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
  },
  fileTab: {
    fontFamily: 'monospace',
    fontSize: '0.6875rem',
    height: 22,
    cursor: 'pointer',
  },
  diff: {
    flex: 1,
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    background:
      theme.palette.type === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
  },
  row: { display: 'flex', whiteSpace: 'pre', minWidth: 'max-content' },
  add: { backgroundColor: 'rgba(46, 160, 67, 0.16)' },
  del: { backgroundColor: 'rgba(248, 81, 73, 0.16)' },
  fold: {
    color: theme.palette.text.hint,
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
    fontStyle: 'italic',
    padding: theme.spacing(0.25, 0),
    textAlign: 'center',
    fontSize: '0.6875rem',
  },
  gutter: {
    width: 88,
    flexShrink: 0,
    textAlign: 'right',
    paddingRight: theme.spacing(1),
    color: theme.palette.text.hint,
    userSelect: 'none',
  },
  sign: { width: 16, flexShrink: 0, textAlign: 'center' },
  lineText: { paddingLeft: theme.spacing(1) },
  empty: { color: theme.palette.text.secondary, fontSize: '0.875rem', padding: theme.spacing(2) },
  hint: { color: theme.palette.text.secondary, fontSize: '0.8125rem', padding: theme.spacing(0, 2, 1.5) },
  doneMsg: { padding: theme.spacing(3), fontSize: '0.9375rem' },
}));

const post = async (
  base: string,
  fetchApi: { fetch: typeof fetch },
  changes: Change[],
  dryRun: boolean,
): Promise<any> => {
  const res = await fetchApi.fetch(`${base}/teams/changes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ changes, dryRun }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || String(res.status));
  return json;
};

// Short wall-clock prefix for process-log lines (HH:MM:SS), mirroring the
// scaffolder task log's timestamped feel.
const ts = (): string =>
  new Date().toLocaleTimeString([], { hour12: false });

const ROW_SIGN: Record<'ctx' | 'add' | 'del', string> = {
  ctx: ' ',
  add: '+',
  del: '−',
};

// Renders one affected file as a unified, folded diff.
const FileDiff = ({ file }: { file: DiffFile }) => {
  const classes = useStyles();
  const ROW_CLASS: Record<'ctx' | 'add' | 'del', string> = {
    ctx: '',
    add: classes.add,
    del: classes.del,
  };
  const rows = useMemo(
    () => foldDiff(lineDiff(file.before, file.after)),
    [file.before, file.after],
  );
  return (
    <div className={classes.diff}>
      {rows.map((r, idx) => {
        if (r.t === 'fold') {
          return (
            <div key={`f${idx}`} className={classes.fold}>
              ⋯ {r.count} unchanged line{r.count === 1 ? '' : 's'}
            </div>
          );
        }
        const cls = ROW_CLASS[r.t];
        const sign = ROW_SIGN[r.t];
        return (
          <div key={idx} className={`${classes.row} ${cls}`}>
            <span className={classes.gutter}>
              {r.oldNo ?? ''} {r.newNo ?? ''}
            </span>
            <span className={classes.sign}>{sign}</span>
            <span className={classes.lineText}>{r.text || ' '}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Split review surface. Left: every staged change (drop any). Right: a live
 * `git diff` of each .github-private file the batch would touch, recomputed
 * whenever the change set changes. Open PR commits the whole set as one PR.
 */

/**
 * Reports the first moment its child is actually VISIBLE -- laid out with a
 * non-zero box. `rightTab === 'diff'` only says the diff is mounted; on a
 * narrow screen the pane could be mounted at 0x0, which is exactly the state
 * that let Open PR promise a review nobody could perform.
 */
function DiffSeen({
  onSeen,
  children,
}: {
  onSeen: () => void;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 40 && r.height > 40) onSeen();
    };
    check();
    // ResizeObserver, so a pane that starts collapsed and later gets space
    // (orientation change, tab switch) still satisfies the gate.
    if (typeof ResizeObserver === 'undefined') {
      // Older engines: fall back to a single deferred re-check rather than
      // leaving the gate permanently shut.
      const t = setTimeout(check, 250);
      return () => clearTimeout(t);
    }
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onSeen]);
  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {children}
    </div>
  );
}

export const TeamReviewDialog = ({
  open,
  changes,
  onRemove,
  onClose,
  onCommitted,
}: {
  open: boolean;
  changes: StagedChange[];
  onRemove: (id: number) => void;
  onClose: () => void;
  onCommitted: () => void;
}) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [activeFile, setActiveFile] = useState(0);
  // Right pane shows the diff while reviewing; flips to the process log while
  // opening the PR (and stays there for the result). Manual toggle in 'ready'.
  const [rightTab, setRightTab] = useState<'diff' | 'log'>('diff');
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('xs'));
  /**
   * Has the diff actually been on screen?
   *
   * The diff is this product's trust artifact -- the whole promise is "nothing
   * ships that you have not reviewed". On a phone the diff pane used to be 0px
   * wide while Open PR stayed enabled, so the promise could be broken without
   * the user doing anything wrong. Gating the submit on the diff having
   * RENDERED (not merely existing) makes the promise structural. Desktop shows
   * the diff by default, so this is satisfied the moment the preview lands and
   * costs a normal user nothing.
   */
  const [diffSeen, setDiffSeen] = useState(false);
  useEffect(() => {
    if (!open) setDiffSeen(false);
  }, [open]);
  const markDiffSeen = useCallback(() => setDiffSeen(true), []);
  const [log, setLog] = useState<string[]>([]);
  const append = (...lines: string[]) =>
    setLog(prev => [...prev, ...lines.map(l => `${ts()} ${l}`)]);

  const body = useMemo(() => changes.map(c => c.change), [changes]);

  // Recompute the dry-run diff whenever the dialog opens or the set changes.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLog([`${ts()} Validating ${changes.length} staged change(s)…`]);
    setRightTab('diff');
    if (changes.length === 0) {
      setPreview({ effective: 0, noops: 0, files: [] });
      setPhase({ kind: 'ready' });
      return undefined;
    }
    setPhase({ kind: 'loading' });
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('team-admin');
        const r = await post(base, fetchApi, body, true);
        if (cancelled) return;
        const results = (r.results ?? []) as {
          summary: string;
          changed: boolean;
        }[];
        const files = (r.files ?? []) as DiffFile[];
        setPreview({
          effective: r.effectiveChanges ?? 0,
          noops: results.filter(x => !x.changed).length,
          files,
        });
        setActiveFile(0);
        append(
          `Validated: ${r.effectiveChanges ?? 0} change(s) will apply across ${
            files.length
          } file(s).`,
          ...results.map(
            x => `  ${x.changed ? '•' : '– (no-op)'} ${x.summary}`,
          ),
        );
        setPhase({ kind: 'ready' });
      } catch (e) {
        if (!cancelled) {
          append(`✗ Validation failed: ${(e as Error).message}`);
          setPhase({ kind: 'error', message: (e as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, body, changes.length, discoveryApi, fetchApi]);

  const openPr = async () => {
    setPhase({ kind: 'opening' });
    setRightTab('log');
    append('Opening pull request against .github-private…');
    try {
      const base = await discoveryApi.getBaseUrl('team-admin');
      const r = await post(base, fetchApi, body, false);
      append(
        r.prUrl
          ? `✓ Pull request #${r.prNumber} opened: ${r.prUrl}`
          : 'No effective change; no PR opened.',
      );
      setPhase({ kind: 'done', prUrl: r.prUrl, prNumber: r.prNumber });
    } catch (e) {
      append(`✗ Failed to open PR: ${(e as Error).message}`);
      setPhase({ kind: 'error', message: (e as Error).message });
    }
  };

  const close = () => {
    const committed = phase.kind === 'done';
    setPhase({ kind: 'loading' });
    setPreview(null);
    setLog([]);
    setRightTab('diff');
    if (committed) onCommitted();
    else onClose();
  };

  const files = preview?.files ?? [];
  const busy = phase.kind === 'opening' || phase.kind === 'loading';

  const logPanel = (
    <div className={classes.logWrap}>
      <LogViewer text={log.join('\n')} />
    </div>
  );

  const renderDiff = () => {
    if (files.length === 0) {
      return (
        <Typography className={classes.empty}>
          No file changes — the staged edits are all no-ops against the current
          Terraform.
        </Typography>
      );
    }
    const file = files[Math.min(activeFile, files.length - 1)];
    return (
      <>
        {files.length > 1 && (
          <div className={classes.fileTabs}>
            {files.map((f, idx) => (
              <Chip
                key={f.path}
                className={classes.fileTab}
                size="small"
                label={f.path.split('/').pop()}
                color={idx === activeFile ? 'primary' : 'default'}
                variant={idx === activeFile ? 'default' : 'outlined'}
                onClick={() => setActiveFile(idx)}
              />
            ))}
          </div>
        )}
        <Typography className={classes.hint}>{file.path}</Typography>
        <FileDiff file={file} />
      </>
    );
  };

  const renderRight = () => {
    if (phase.kind === 'loading') return <Progress />;

    // After Open PR: result banner + the process log.
    if (phase.kind === 'done' || phase.kind === 'error') {
      const ok = phase.kind === 'done' && Boolean(phase.prUrl);
      let bannerTitle: string;
      let bannerBody: string;
      if (phase.kind === 'error') {
        bannerTitle = "Couldn't open the pull request";
        bannerBody = phase.message;
      } else if (ok) {
        bannerTitle = `Pull request #${phase.prNumber} opened`;
        bannerBody =
          'Nothing changes until atlantis apply is run (before merge).';
      } else {
        bannerTitle = 'No effective change';
        bannerBody =
          'The staged edits were all no-ops against the current Terraform.';
      }
      return (
        <>
          {/* role=status + aria-live: the outcome of the riskiest action on the
              page must ANNOUNCE itself. Without it a screen-reader user had to
              re-explore the dialog to learn whether the PR opened. assertive on
              failure only -- a success can wait for a natural pause, a failure
              should not. */}
          <div
            className={classes.banner}
            role="status"
            aria-live={phase.kind === 'error' ? 'assertive' : 'polite'}
          >
            {phase.kind === 'done' ? (
              <CheckCircleIcon className={classes.bannerIconOk} />
            ) : (
              <ErrorOutlineIcon className={classes.bannerIconErr} />
            )}
            <div>
              <div className={classes.bannerTitle}>{bannerTitle}</div>
              <div className={classes.bannerBody}>{bannerBody}</div>
            </div>
            {ok && phase.prUrl && (
              <LinkButton
                className={classes.bannerAction}
                to={phase.prUrl}
                color="primary"
                variant="contained"
                endIcon={<OpenInNewIcon />}
              >
                View pull request
              </LinkButton>
            )}
          </div>
          {logPanel}
        </>
      );
    }

    // Opening: show the live log.
    if (phase.kind === 'opening') return logPanel;

    // Reviewing: Diff | Log toggle.
    return (
      <>
        <div className={classes.viewTabs}>
          <Chip
            className={classes.viewTab}
            size="small"
            clickable
            label="Diff"
            color={rightTab === 'diff' ? 'primary' : 'default'}
            variant={rightTab === 'diff' ? 'default' : 'outlined'}
            onClick={() => setRightTab('diff')}
          />
          <Chip
            className={classes.viewTab}
            size="small"
            clickable
            label="Log"
            color={rightTab === 'log' ? 'primary' : 'default'}
            variant={rightTab === 'log' ? 'default' : 'outlined'}
            onClick={() => setRightTab('log')}
          />
        </div>
        {rightTab === 'diff' ? <DiffSeen onSeen={markDiffSeen}>{renderDiff()}</DiffSeen> : logPanel}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="lg"
      fullWidth
      fullScreen={narrow}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle>Review changes ({changes.length})</DialogTitle>
      <DialogContent className={classes.body} dividers>
        <div className={classes.left}>
          <Typography className={classes.leftHeader}>
            Staged changes ({changes.length})
          </Typography>
          <div className={classes.leftScroll}>
            {changes.length === 0 ? (
              <Typography className={classes.empty}>
                No staged changes.
              </Typography>
            ) : (
              changes.map(c => (
                <div key={c.id} className={classes.changeRow}>
                  <Chip
                    className={classes.orgChip}
                    size="small"
                    label={c.change.org}
                  />
                  <span className={classes.changeLabel}>{c.label}</span>
                  {!busy && phase.kind !== 'done' && (
                    <IconButton
                      className={classes.changeRemove}
                      size="small"
                      aria-label={`Remove staged change: ${c.label}`}
                      onClick={() => onRemove(c.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              ))
            )}
          </div>
          {preview && phase.kind === 'ready' && (
            <Typography className={classes.summary}>
              {preview.effective} change{preview.effective === 1 ? '' : 's'} across{' '}
              {files.length} file{files.length === 1 ? '' : 's'}
              {preview.noops > 0 && `, ${preview.noops} no-op(s) skipped`}.
              <br />
              Open PR commits all of these to a single{' '}
              <code>.github-private</code> PR.
            </Typography>
          )}
        </div>
        <div className={classes.right}>{renderRight()}</div>
      </DialogContent>
      <DialogActions>
        {phase.kind === 'done' || phase.kind === 'error' ? (
          <Button onClick={close} color="primary">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={phase.kind === 'opening'}>
              Close
            </Button>
            <Tooltip
              title={
                !diffSeen && preview && preview.effective > 0
                  ? 'Open the Diff tab first — this PR ships what the diff shows'
                  : ''
              }
            >
              {/* span: a disabled button fires no events, so Tooltip needs a
                  live target to explain WHY it is disabled. A gate nobody can
                  see is a trap, not a safeguard. */}
              <span>
                <Button
                  onClick={openPr}
                  color="primary"
                  variant="contained"
                  disabled={busy || !preview || preview.effective === 0 || !diffSeen}
                >
                  Open PR
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
